import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverPortal,
} from "@/components/ui/popover";
import { BellRing } from "lucide-react";
import { NotificationItem } from "./notifications-item";
import { NotificationsTabs } from "./notifications-tabs";
import { NotificationsHeader } from "./notifications-header";
import { NotificationsFooter } from "./notifications-footer";
import { useState, type ReactNode, useRef, useEffect } from "react";
import type { ISingleNotification } from "@/types/notifications.interface";
import { useGetNotificationsInfiniteQuery } from "@/services/notificationsApi";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { NOTIFICATIONS_PLATFORM_FILTER_ENUM } from "@/utils/enums/notifications.enum";

export const NotificationBell = () => {
  const { t } = useTranslation("common");
  const { unreadNotificationsCount } = useAppSelector(selectUser);
  const [shake, setShake] = useState(false);

  // Track previous count
  const prevCountRef = useRef(unreadNotificationsCount);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (unreadNotificationsCount > prevCountRef.current) {
      setShake(true);
      timer = setTimeout(() => setShake(false), 600);
    }

    prevCountRef.current = unreadNotificationsCount;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [unreadNotificationsCount]);

  return (
    <NotificationsList>
      <div
        className="relative group flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sxs hover:bg-primary/10"
        aria-label={t("common:a11y.n_unread_notifications", {
          count: unreadNotificationsCount,
        })}
      >
        {unreadNotificationsCount > 0 && (
          <>
            {/* Ping animation pulses from same anchor as badge */}
            <span className="absolute -top-2 -right-1.5 h-4 w-4 animate-ping rounded-full bg-primary opacity-50" />

            {/* Notification badge */}
            <div className="absolute -top-2 -right-1.5 z-10 flex min-h-4 aspect-square items-center justify-center rounded-full bg-red-500 p-1 text-[9px] font-bold leading-none text-white">
              {unreadNotificationsCount > 99 ? "99" : unreadNotificationsCount}
            </div>
          </>
        )}

        {/* Bell */}
        <img
          src="/icons/interface-icons/shared/icon-3d-bell.png"
          alt={t("nav:modules.notifications")}
          draggable={false}
          className={`size-5.5 object-contain origin-top ${shake ? "animate-bell-vibrate" : ""}`}
        />
      </div>
    </NotificationsList>
  );
};

const NotificationsList = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation(["common", "nav"]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    NOTIFICATIONS_PLATFORM_FILTER_ENUM.MY_SPACE,
  );

  const {
    data: notifications,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
  } = useGetNotificationsInfiniteQuery(
    { limit: 5, platformSectionType: activeTab },
    { skip: !open },
  );

  // Flatten pages
  const allNotifications =
    notifications?.pages.flatMap((page) => page.data.notificationData) || [];

  // Use Redux store count — updated by WebSocket + query, reliable across tab changes
  const { unreadNotificationsCount: unreadCount } = useAppSelector(selectUser);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (!scrollRef.current || isFetching || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // If scrolled near the bottom, fetch next page
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      fetchNextPage();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="relative w-[340px] sm:w-[380px] rounded-xl p-0 me-2 shadow-lg border-border/60 overflow-hidden"
          align="center"
        >
          <div className="flex h-full flex-col">
            <NotificationsHeader
              getAllNotificationRefetch={refetch}
              unreadCount={unreadCount}
              setOpen={setOpen}
            />
            <NotificationsTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="max-h-[380px] min-h-[380px] overflow-y-auto divide-y divide-border/50 scrollbar-thin"
            >
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 px-4 py-3.5"
                  >
                    <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <Skeleton className="h-3 w-full rounded" />
                      <Skeleton className="h-2.5 w-2/3 rounded" />
                    </div>
                  </div>
                ))}
              {/* Empty state */}
              {!isLoading &&
                (!allNotifications || allNotifications.length === 0) && (
                  <div className="flex flex-col items-center justify-center gap-3 min-h-[380px] text-center px-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <BellRing size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("common:inbox.all_caught_up")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t("common:inbox.no_notifications_here")}
                      </p>
                    </div>
                  </div>
                )}
              {/* Notifications list */}
              {!isLoading &&
                allNotifications &&
                allNotifications.length > 0 &&
                allNotifications.map((item: ISingleNotification) => (
                  <NotificationItem
                    key={item.id}
                    notification={item}
                    setOpen={setOpen}
                    getAllNotificationRefetch={refetch}
                  />
                ))}
              {/* Infinite scroll loading */}
              {isFetching &&
                !isLoading &&
                Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 px-4 py-3.5"
                  >
                    <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <Skeleton className="h-3 w-full rounded" />
                      <Skeleton className="h-2.5 w-2/3 rounded" />
                    </div>
                  </div>
                ))}
            </div>
            <NotificationsFooter setOpen={setOpen} activeTab={activeTab} />
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
